import { useCallback } from "react";
import { Controller, Control } from "react-hook-form";
import { Input } from "@heroui/react";

export interface ProductDiscountProps {
  control: Control<any>;
  product: {
    index: number;
    productDiscount?: string;
    productDiscountStatus?: number;
    discountIdCorrected?: boolean;
  };
  isDataReadOnlyOperators: boolean;
  getValues: (name: string) => any;
  setValue: (name: string, value: any) => void;
  handleDiscountChange: (index: number, value: string) => void;
  setModifiedValues: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  customTabIndex?: number;
}

function ProductDiscount({
  control,
  product,
  isDataReadOnlyOperators,
  setValue,
  handleDiscountChange,
  setModifiedValues,
  customTabIndex,
}: Readonly<ProductDiscountProps>) {
  const discountStatus = product.productDiscountStatus ?? 0;
  const discountIdCorrected = product.discountIdCorrected ?? false;

  const getBorderColorClass = useCallback(() => {
    if (discountStatus < 25) {
      return "border-red-500";
    } else if (discountStatus < 75) {
      return "border-orange-300";
    }
    return "border-default-200";
  }, [discountStatus]);

  const handleChange = useCallback(
    (value: string) => {
      setValue(`productDiscount-${product.index}`, value);
      setModifiedValues((prev) => ({ ...prev, [product.index]: true }));
      handleDiscountChange(product.index, value);
    },
    [product.index, setValue, setModifiedValues, handleDiscountChange]
  );

  return (
    <div className="relative w-[100px]">
      <Controller
        name={`productDiscount-${product.index}`}
        control={control}
        defaultValue={product.productDiscount || ""}
        render={({ field }) => (
          <Input
            {...field}
            type="text"
            readOnly={isDataReadOnlyOperators}
            classNames={{
              base: ["min-w-[100px]"],
              inputWrapper: [
                `h-8 ${getBorderColorClass()}`,
                "group-data-[focus-visible=true]:outline-none",
                "group-data-[focus-visible=true]:ring-0",
                "group-data-[focus-visible=true]:ring-offset-0",
                "group-data-[focus-visible=true]:!border-vistagreen",
                "group-data-[focus-visible=true]:!border-2",
                "group-data-[focus-visible=true]:!border-solid",
                "group-data-[hover=true]:outline-none",
                "group-data-[hover=true]:ring-0",
                "group-data-[hover=true]:ring-offset-0",
                "group-data-[hover=true]:!border-vistagreen",
                "group-data-[hover=true]:!border-2",
                "group-data-[hover=true]:!border-solid",
                "group-data-[hover=true]:!bg-default-100",
              ],
            }}
            onFocus={(e) => {
              e.target.select();
            }}
            onChange={(e) => {
              field.onChange(e.target.value);
              handleChange(e.target.value);
            }}
            value={field.value || ""}
            variant={
              discountStatus < 75 && !discountIdCorrected
                ? "bordered"
                : "flat"
            }
            tabIndex={customTabIndex}
          />
        )}
      />
    </div>
  );
}

export default ProductDiscount;
